<template>
    <v-sheet width="100%" class="bg-transparent">
        <v-card class="bg-grey-darken-4" variant="outlined" height="250">
            <v-card-text class="pa-0 fill-height d-flex flex-column" style="position: relative;">
                <div v-if="targetIds.length === 0"
                    class="d-flex fill-height align-center justify-center text-grey">
                    Select target entities to view DPS graph
                </div>
                <div v-show="targetIds.length > 0" ref="chartContainer" class="fill-height"
                    style="width: 100%; min-height: 200px;"></div>
            </v-card-text>
        </v-card>
    </v-sheet>
</template>

<script lang="ts">
import { defineComponent, inject, ref, onMounted, onUnmounted, watch, markRaw, Ref, shallowRef, computed, PropType } from 'vue';
import * as echarts from 'echarts';
import { ActorManager } from '@/eventActor';
import { anonymizeMode, getPrimaryClass } from '@/store';
import { formatDuration, getPlayerColor } from '@/util';

export default defineComponent({
    name: 'DpsGraph',
    props: {
        targetIds: { type: Array as PropType<string[]>, required: true }
    },
    setup(props) {
        const actorManager = inject('actorManager') as Ref<ActorManager>;
        const raceNameMap = inject('raceNameMap') as Ref<Record<number, string>>;
        const appEvent = inject('appEvent') as Ref<EventTarget>;

        const chartContainer = ref<HTMLElement | null>(null);
        const chartInstance = shallowRef<echarts.ECharts | null>(null);

        // Shared Target List logic (mirrors ApplyDamageAnalysis)


        // Configuration
        const SLIDING_WINDOW_SIZE = 10; // seconds

        const initChart = () => {
            if (!chartContainer.value || chartInstance.value) return;
            const chart = echarts.init(chartContainer.value, 'dark');
            chartInstance.value = markRaw(chart);
            updateChart();
        };

        const updateChart = () => {
            if (!chartInstance.value) return;
            if (props.targetIds.length === 0) {
                chartInstance.value.clear();
                return;
            }

            const players = Object.values(actorManager.value.entityMap)
                .filter((e: any) => e.isPC || e.IsPC);
            const targetIds = props.targetIds;

            // Sort players globally by their total damage to the selected targets
            // This ensures getPlayerColor(idx) aligns perfectly with the damage breakdown list rank
            const playersWithDamage = players.map((p: any) => {
                let totalDamage = 0;
                targetIds.forEach(tid => {
                    const stats = p.aggregatedStats[tid];
                    if (stats) totalDamage += stats.totalDamage || 0;
                });
                return { p, totalDamage };
            }).filter(item => item.totalDamage > 0)
              .sort((a, b) => b.totalDamage - a.totalDamage)
              .map(item => item.p);

            // 1. Determine Global Time Range
            let minSec = Number.MAX_VALUE;
            let maxSec = -1;

            // Track global timeline for all selected targets
            const globalTimeline: Record<number, number> = {};
            const playerDpsTimelinesData: { p: any, className: string, color: string, timeline: Record<number, number> }[] = [];
            const classCounts: Record<string, number> = {};

            playersWithDamage.forEach((p, idx) => {
                const combinedTimeline: Record<number, number> = {};
                let hasDmg = false;

                targetIds.forEach(tid => {
                    const stats = p.aggregatedStats[tid];
                    if (stats) {
                        hasDmg = true;
                        for (const [sec, dmg] of Object.entries(stats.timeline)) {
                            const s = +sec;
                            combinedTimeline[s] = (combinedTimeline[s] || 0) + (dmg as number);
                            globalTimeline[s] = (globalTimeline[s] || 0) + (dmg as number);
                            if (s < minSec) minSec = s;
                            if (s > maxSec) maxSec = s;
                        }
                    }
                });

                if (hasDmg) {
                    playerDpsTimelinesData.push({
                        p,
                        className: getPrimaryClass(p) || 'Player',
                        color: getPlayerColor(idx),
                        timeline: combinedTimeline
                    });
                }
            });

            if (maxSec === -1) {
                chartInstance.value.setOption({
                    title: { text: 'No damage data found for selection', left: 'center', top: 'center' },
                    series: []
                }, true);
                return;
            }

            const playerDpsTimelines = playerDpsTimelinesData.map(data => {
                classCounts[data.className] = (classCounts[data.className] || 0) + 1;
                return {
                    name: anonymizeMode.value ? `${data.className} ${classCounts[data.className]}` : data.p.name,
                    color: data.color,
                    dps: calculateSlidingDps(data.timeline, minSec, maxSec)
                };
            });

            // 2. Prepare Echarts Options
            const secondsRange: number[] = [];
            const activeSeconds = new Set<number>();

            for (let s = minSec; s <= maxSec; s++) {
                secondsRange.push(s);
                // Determine if this second is part of an active combat phase (target was hit within the sliding window)
                let globalWindowSum = 0;
                for (let i = 0; i < SLIDING_WINDOW_SIZE; i++) {
                    globalWindowSum += globalTimeline[s - i] || 0;
                }
                if (globalWindowSum > 0) {
                    activeSeconds.add(s);
                }
            }

            // Recalculate true DPS boundaries over the full global timeline correctly aligning to the target's lifespan
            playerDpsTimelines.forEach(pData => {
                // Ensure data arrays exactly match the secondsRange mapping, yielding null during mech gaps
                (pData as any).seriesData = secondsRange.map(s => {
                    if (!activeSeconds.has(s)) return null;
                    return pData.dps[s] || 0; // if active combat but this player did nothing, returns 0
                });
            });

            const series: any[] = playerDpsTimelines.map(pData => {
                return {
                    name: pData.name,
                    type: 'line',
                    smooth: true,
                    showSymbol: false,
                    connectNulls: false, // Ensure gaps are maintained for mech stages
                    data: (pData as any).seriesData,
                    lineStyle: { color: pData.color, width: 3 },
                    itemStyle: { color: pData.color },
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0, color: pData.color + '66' },
                            { offset: 1, color: pData.color + '00' }
                        ])
                    }
                };
            });

            const option: echarts.EChartsOption = {
                backgroundColor: 'transparent',
                tooltip: {
                    trigger: 'axis',
                    formatter: (params: any) => {
                        let res = `Time: ${formatDuration(params[0].axisValue - minSec)}<br/>`;
                        params
                            .sort((a: any, b: any) => (b.value || 0) - (a.value || 0))
                            .forEach((item: any) => {
                                if (item.value != null) {
                                    res += `${item.marker} ${item.seriesName}: <b>${item.value.toLocaleString()}</b> DPS<br/>`;
                                }
                            });
                        return res;
                    },
                    backgroundColor: 'rgba(30, 30, 30, 0.9)',
                    borderColor: '#444',
                    textStyle: { color: '#eee' }
                },
                legend: {
                    data: series.map(s => s.name),
                    top: 10,
                    textStyle: { color: '#ccc' }
                },
                grid: {
                    left: '3%',
                    right: '4%',
                    bottom: '5%',
                    top: '60px',
                    containLabel: true
                },
                xAxis: {
                    type: 'category',
                    boundaryGap: false,
                    data: secondsRange,
                    axisLabel: {
                        formatter: (val: string) => formatDuration(+val - minSec),
                        color: '#999'
                    },
                    axisLine: { lineStyle: { color: '#444' } },
                    splitLine: { show: true, lineStyle: { color: '#222' } }
                },
                yAxis: {
                    type: 'value',
                    name: 'DPS',
                    axisLabel: { color: '#999' },
                    axisLine: { lineStyle: { color: '#444' } },
                    splitLine: { lineStyle: { color: '#222' } }
                },
                series: series as any
            };

            chartInstance.value.setOption(option, true);
        };

        const calculateSlidingDps = (timeline: Record<number, number>, globalMin: number, globalMax: number): Record<number, number> => {
            const result: Record<number, number> = {};
            if (globalMin > globalMax) return result;

            // For each second in the raid range, calculate the 10s average over the target's entire lifespan
            for (let s = globalMin; s <= globalMax; s++) {
                let windowSum = 0;
                for (let i = 0; i < SLIDING_WINDOW_SIZE; i++) {
                    windowSum += timeline[s - i] || 0;
                }
                result[s] = Math.round(windowSum / SLIDING_WINDOW_SIZE);
            }
            return result;
        };

        const handleResize = () => {
            chartInstance.value?.resize();
        };

        const onClear = () => {
            if (chartInstance.value) {
                chartInstance.value.clear();
            }
        };

        let resizeObserver: ResizeObserver | null = null;

        onMounted(() => {
            appEvent.value.addEventListener('clear', onClear);
            // Wait for v-show to potentially activate chartContainer
            setTimeout(initChart, 200);

            if (chartContainer.value) {
                resizeObserver = new ResizeObserver(() => {
                    handleResize();
                });
                resizeObserver.observe(chartContainer.value);
            }
        });

        onUnmounted(() => {
            appEvent.value.removeEventListener('clear', onClear);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            chartInstance.value?.dispose();
        });

        watch(() => props.targetIds, () => {
            if (!chartInstance.value) {
                initChart();
            }
            updateChart();
        }, { deep: true });

        watch(anonymizeMode, () => {
            updateChart();
        });

        // Also watch actorManager for live updates during combat
        watch(actorManager, () => {
            updateChart();
        }, { deep: true });

        return {
            chartContainer,
            targetIds: computed(() => props.targetIds)
        };
    }
});
</script>

<style scoped>
.fill-height {
    height: 100%;
}
</style>
